import { usePrograms } from '@/hooks/use-programs';
import { collectProgramIdsFromPack } from '@/utils/pack-program';
import {
    MediaNestedPack,
    MediaPack,
    MediaPackGuide,
    MediaPackProgram,
    RbcCircleRec,
} from '@calm/evexia-web-sdk';
import { CardProps } from '@rhg/components/card';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import commonMessages from '@rhg/lang/messages/common';
import { SxProps } from '@rhg/libs/material';
import {
    NumberByBreakpoint,
    getValueByBreakpoint,
    sxAppend,
} from '@rhg/libs/material/sx';
import { PackProvider } from '@rhg/providers/pack-provider';
import { newAndNoteworthyPack, forYouPack } from '@rhg/utils/constants';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Carousel } from '../carousel';
import { FullscreenCarousel } from '../carousel/fullscreen';
import { CarouselProps } from '../carousel/types';
import { ChipLabel } from '../chip-label';
import { PackGuideCard } from '../pack-guide-card';
import { PackPackCard } from '../pack-pack-card';
import { PackProgramCard } from '../pack-program-card';
import {
    CardSizingProps,
    CarouselSkeleton,
    DiscoverQuickNavCarouselSkeleton,
    SkeletonCardVariant,
} from '../ui/skeletons';

interface SkeletonCardSizingProps extends CardSizingProps {
    addContainerSpacing?: boolean;
}

export interface PackCarouselProps extends CarouselProps {
    pack?: MediaPack;
    cardVariant: CardProps['variant'];
    recommendation?: RbcCircleRec;
    isFullscreen?: boolean;
    maxLength?: number;
    /** The spacing around each item (Card Width = itemWidth - itemSpacing)*/
    itemSpacing: NumberByBreakpoint;
    isFetched: boolean;
    allowPackOfPacks?: boolean;
    sortAlphabetically?: boolean;
    contentTypeAsCategory?: boolean;
    skeletonType?: SkeletonCardVariant;
    skeletonCardSizingProps?: SkeletonCardSizingProps;
    cardSx?: SxProps;
    cardContentSx?: SxProps;
}

export type PackItemsType = (
    | MediaNestedPack
    | MediaPackGuide
    | MediaPackProgram
)[];
export interface PackItemsProps
    extends Pick<
        PackCarouselProps,
        'cardVariant' | 'recommendation' | 'maxLength' | 'itemSpacing'
    > {
    packItems: PackItemsType;
    packClass: string;
    cardWidth: number;
}

const styleChipLabel = {
    m: 4,
};
const styleChipLabelHorizontal = { m: 2 };

function NewChipLabel({ variant }: { variant: CardProps['variant'] }) {
    const { formatMessage } = useIntl();
    return (
        <ChipLabel
            sx={sxAppend(
                styleChipLabel,
                variant === 'horizontal' ? styleChipLabelHorizontal : undefined
            )}
            variant="light"
            label={formatMessage(commonMessages['common.new'])}
        />
    );
}

export function PackCarousel(props: PackCarouselProps) {
    const {
        pack,
        recommendation,
        cardVariant,
        isFullscreen,
        itemWidth,
        maxLength,
        itemSpacing = 0,
        firstItemOffsetPx,
        isFetched: _isFetched,
        skeletonType = 'clinical',
        skeletonCardSizingProps,
        allowPackOfPacks = false,
        sortAlphabetically = false,
        cardSx,
        cardContentSx,
        contentTypeAsCategory,
        ...carouselProps
    } = props;
    const currentBreakpoint = useCurrentBreakpointName();

    // Prefetch programs for caching
    const nestedProgramIds = collectProgramIdsFromPack(pack);
    const { isFetched: isFetchedPacks, isEnabled } = usePrograms([
        { programIds: nestedProgramIds, packClass: pack?.packClass ?? '' },
    ]);
    const isFetched = _isFetched && (isFetchedPacks || !isEnabled);

    const itemWidthValue = useMemo(
        () =>
            getValueByBreakpoint<number>(itemWidth ?? 0, currentBreakpoint) ??
            0,
        [currentBreakpoint, itemWidth]
    );

    const itemSpacingValue = useMemo(
        () =>
            getValueByBreakpoint<number>(itemSpacing ?? 0, currentBreakpoint) ??
            0,
        [currentBreakpoint, itemSpacing]
    );

    const cardWidth = useMemo(
        () => itemWidthValue - itemSpacingValue,
        [itemSpacingValue, itemWidthValue]
    );

    if (!isFetched) {
        return skeletonType === 'quickNav' ? (
            <DiscoverQuickNavCarouselSkeleton cardWidth={cardWidth} />
        ) : (
            <CarouselSkeleton
                cardHeight="100%"
                variant={skeletonType}
                cardWidth={itemWidth}
                {...skeletonCardSizingProps}
                count={6}
                imageHeight="auto"
                imageAspectRatio="3/2"
            />
        );
    }

    const filteredItems = pack?.items?.filter(
        (item) =>
            !!item &&
            (item.type === 'guide' ||
                item.type === 'program' ||
                (allowPackOfPacks && item.type === 'pack'))
    );
    const items = maxLength
        ? filteredItems?.slice(0, maxLength)
        : filteredItems;

    const displayItems = sortAlphabetically
        ? items?.sort((a, b) => {
              if (a?.title && b?.title) {
                  return a.title.localeCompare(b.title);
              }
              return -1;
          })
        : items;
    const showNewChipLabel =
        pack?.packClass?.includes(newAndNoteworthyPack) ||
        pack?.packClass?.includes(forYouPack);

    if (isFullscreen) {
        return (
            <PackProvider packTitle={pack?.title}>
                <FullscreenCarousel
                    {...carouselProps}
                    itemWidth={itemWidth}
                    itemSpacing={itemSpacing}
                    itemsDisplayed={items?.length}
                    firstItemOffsetPx={firstItemOffsetPx}
                >
                    {displayItems?.map((item) => {
                        if (item.type === 'guide') {
                            const guideItem = item as MediaPackGuide;
                            return (
                                <PackGuideCard
                                    key={guideItem.guideId}
                                    willLockIfUnsponsored
                                    packClass={pack?.packClass ?? ''}
                                    variant={cardVariant}
                                    packGuide={guideItem}
                                    recommendation={recommendation}
                                    sx={cardSx}
                                    contentSx={cardContentSx}
                                    CardProps={{
                                        imageWidth: itemWidth,
                                        ...(showNewChipLabel
                                            ? {
                                                  imageIconTop: (
                                                      <NewChipLabel
                                                          variant={cardVariant}
                                                      />
                                                  ),
                                              }
                                            : {}),
                                    }}
                                    {...(contentTypeAsCategory
                                        ? {
                                              categoryTitle:
                                                  guideItem?.contentType,
                                          }
                                        : {})}
                                />
                            );
                        } else if (allowPackOfPacks && item.type === 'pack') {
                            const packItem = item as MediaNestedPack;
                            return (
                                <PackPackCard
                                    key={packItem.packClass}
                                    pack={packItem}
                                    displayType={pack?.displayType}
                                    cardSx={cardSx}
                                    cardContentSx={cardContentSx}
                                    cardVariant={cardVariant}
                                    CardProps={{ imageWidth: itemWidth }}
                                />
                            );
                        } else {
                            const programItem = item as MediaPackProgram;
                            return (
                                <PackProgramCard
                                    key={programItem.programId}
                                    packClass={pack?.packClass ?? ''}
                                    variant={cardVariant}
                                    packProgram={programItem}
                                    recommendation={recommendation}
                                    contentSx={cardContentSx}
                                    CardProps={{ imageWidth: itemWidth }}
                                    {...(contentTypeAsCategory
                                        ? {
                                              categoryTitle:
                                                  programItem?.contentType,
                                          }
                                        : {})}
                                />
                            );
                        }
                    })}
                </FullscreenCarousel>
            </PackProvider>
        );
    }

    return (
        <PackProvider packTitle={pack?.title}>
            <Carousel
                {...carouselProps}
                itemSpacing={itemSpacing}
                itemsDisplayed={items?.length}
                itemWidth={itemWidth}
                firstItemOffsetPx={firstItemOffsetPx}
            >
                {items?.map((item) => {
                    if (item.type === 'guide') {
                        const guideItem = item as MediaPackGuide;
                        return (
                            <PackGuideCard
                                key={guideItem.guideId}
                                willLockIfUnsponsored
                                packClass={pack?.packClass ?? ''}
                                variant={cardVariant}
                                packGuide={guideItem}
                                recommendation={recommendation}
                                CardProps={{ imageWidth: itemWidth }}
                                {...(contentTypeAsCategory
                                    ? { categoryTitle: guideItem?.contentType }
                                    : {})}
                            />
                        );
                    } else if (allowPackOfPacks && item.type === 'pack') {
                        const packItem = item as MediaNestedPack;
                        return (
                            <PackPackCard
                                key={packItem.packClass}
                                pack={packItem}
                                displayType={pack?.displayType}
                                cardSx={cardSx}
                                cardContentSx={cardContentSx}
                                cardVariant={cardVariant}
                                CardProps={{ imageWidth: itemWidth }}
                            />
                        );
                    } else {
                        const programItem = item as MediaPackProgram;
                        return (
                            <PackProgramCard
                                key={programItem.programId}
                                packClass={pack?.packClass ?? ''}
                                variant={cardVariant}
                                packProgram={programItem}
                                recommendation={recommendation}
                                CardProps={{ imageWidth: itemWidth }}
                                {...(contentTypeAsCategory
                                    ? {
                                          categoryTitle:
                                              programItem?.contentType,
                                      }
                                    : {})}
                            />
                        );
                    }
                })}
            </Carousel>
        </PackProvider>
    );
}
